/**
 * O ENQUADRAMENTO SAI EM INGLÊS, E SÓ ELE — `T1.33`, `T6.3`.
 *
 * ── A LETRA DELE, E O NÚMERO QUE ELA GANHOU ─────────────────────────────────────────────────
 *
 * *"Criação de HQ irresponsável — ignora os comandos (…) entendeu 'vista de cima' como um mapa."*
 *
 * Medido em 29/09 contra produção (`rastro/medir-o-enquadramento-em-duas-linguas.mjs`): a mesma cena,
 * a mesma semente, e a única diferença sendo a LÍNGUA da frase de enquadramento.
 *
 *   `ângulo de câmera alto, visto de cima para baixo`   **0 de 4** — a câmera fica no nível dos olhos
 *   `high camera angle, top-down view, seen from above` **5 de 5** — a câmera fica acima, olhando para baixo
 *
 * E na amostra de aderência do mesmo dia, 24 dos 30 elementos pedidos apareceram — **e o único que
 * nunca apareceu foi o enquadramento**, 0 de 6. Não é o modelo ignorando o comando: é o comando
 * chegando numa língua que ele não lê.
 *
 * ── POR QUE ISTO NÃO É "INVENTAR UM TRADUTOR" ───────────────────────────────────────────────
 *
 * Esta casa recusou, no `T5.2`, substituir a tradução de cena por LLM por um glossário local: a cena
 * é texto livre, e um glossário palavra a palavra entregaria pior do que o português cru.
 *
 * O enquadramento é outra coisa: é um **vocabulário FECHADO** — as oito frases que o próprio produto
 * escreveu (`CAMERA_ANGLE_PRESETS`) mais quatro termos de câmera que a criança digita à mão. Traduzir
 * doze constantes não tem nada em comum com traduzir uma história: não há ambiguidade, não há
 * concordância, não há sujeito para perder.
 *
 * ── E A FRASE EM PORTUGUÊS CONTINUA NA TELA ─────────────────────────────────────────────────
 *
 * O botão de enquadramento **escreve a frase dentro da caixa de texto que a criança lê e edita**. Por
 * isso a tradução não pode morar no botão: trocar a frase ali poria inglês na frente de uma criança
 * brasileira, que é exatamente a queixa do `T1.4` (*"tá em inglês, as coisas estão em inglês"*) e o
 * que a varredura de português cobra em todo texto de tela.
 *
 * Então a troca acontece **na saída**, uma linha antes de a requisição partir: a criança escreve e lê
 * português, e o modelo recebe inglês.
 *
 * ── O QUE ELA NÃO FAZ ───────────────────────────────────────────────────────────────────────
 *
 * Nada além das frases desta tabela. Um pedido que não contenha nenhuma delas atravessa **byte por
 * byte igual** — e é isso que faz esta peça ser reversível de verdade: sem as frases, ela não existe.
 *
 * E as frases de POSE (`POSE_PRESETS`: "de perfil", "visto de costas"…) ficaram de fora de propósito:
 * elas têm a mesma forma e **não foram medidas**. Na amostra de 29/09 a pose pedida apareceu em 6 de
 * 6 — então não há defeito medido para consertar, e mexer nelas seria mexer no que está funcionando.
 */

/**
 * O vocabulário de enquadramento, e o inglês que o modelo lê.
 *
 * As oito primeiras são a frase EXATA que cada botão escreve na caixa. Se alguém mudar o texto do botão sem mudar
 * esta tabela, a tradução simplesmente para de acontecer — e o teste
 * `enquadramento-em-ingles.test.ts` recusa exatamente isso, comparando as duas listas.
 */
export const ENQUADRAMENTO: Record<string, string> = {
  "câmera ao nível dos olhos": "eye-level camera angle",
  "ângulo de câmera baixo, visto de baixo para cima":
    "low camera angle, worm's-eye view, seen from below looking up",
  "ângulo de câmera alto, visto de cima para baixo":
    "high camera angle, top-down view, seen from above looking down",
  "vista aérea de cima (bird's eye view)": "aerial bird's-eye view directly from above",
  "ângulo holandês inclinado": "dutch angle, tilted camera",
  "close-up no rosto": "close-up shot of the face",
  "plano médio, da cintura para cima": "medium shot, from the waist up",
  "plano aberto, corpo inteiro": "wide shot, full body in frame",

  // ── E AS QUATRO QUE ELE DIGITOU À MÃO, porque a letra dele não veio de um botão ────────────
  //
  // O `T1.33` é *"pedi 'vista de cima' e ele entendeu como um mapa"*. Ele **digitou** a frase; não
  // clicou em botão nenhum. Cobrir só as oito frases dos botões consertaria o caminho que ele não
  // usou, e a medição de 29/09 diz que o defeito é da LÍNGUA, não do botão.
  //
  // Estas quatro continuam sendo vocabulário FECHADO — são termos de enquadramento e nada mais. O
  // que fica de fora é tudo o que exigiria adivinhar: "por cima do ombro", "de longe", "bem perto"
  // são frases de cena tanto quanto de câmera, e traduzir por conta própria ali é o começo do
  // tradutor de texto livre que esta casa recusou no `T5.2`.
  "visto de cima": "top-down view, seen from above",
  "vista de cima": "top-down view, seen from above",
  "visto de baixo": "worm's-eye view, seen from below",
  "vista de baixo": "worm's-eye view, seen from below",
};

/**
 * Troca as frases de enquadramento conhecidas pelo equivalente em inglês.
 *
 * A comparação ignora maiúsculas e acentos porque a frase chega da caixa de texto, onde a criança
 * pode ter editado — e uma tradução que só funciona com o texto intocado é uma tradução que quase
 * nunca funciona.
 *
 * A ordem importa: as frases mais LONGAS primeiro. "ângulo de câmera alto, visto de cima para baixo"
 * contém pedaços que também aparecem noutras frases, e trocar a curta antes deixaria a longa
 * quebrada no meio.
 */
export function enquadramentoEmIngles(prompt: string): string {
  if (!prompt) return prompt;

  /**
   * Minúsculas e sem acento, e o resultado tem SEMPRE o mesmo comprimento da entrada.
   *
   * A faixa vai escrita como `\u0300-\u036f` e não com os caracteres em si: os sinais combinantes
   * são invisíveis num editor, e uma faixa que ninguém vê é uma faixa que alguém apaga sem perceber.
   */
  const semAcento = (t: string) =>
    t
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  // `NFC` PRIMEIRO, e este é o detalhe que faz a posição valer: numa string composta cada letra
  // acentuada é UM caractere, então tirar o acento devolve outro de UM caractere e o índice achado
  // na forma sem acento aponta para o mesmo lugar na original. Numa string já decomposta — que um
  // teclado de macOS produz — os comprimentos não bateriam e a troca cortaria no lugar errado.
  let saida = prompt.normalize("NFC");

  // AS MAIS LONGAS PRIMEIRO: "ângulo de câmera alto, visto de cima para baixo" contém pedaços que
  // também aparecem em outras frases, e trocar a curta antes deixaria a longa quebrada no meio.
  const chaves = Object.keys(ENQUADRAMENTO).sort((a, b) => b.length - a.length);

  for (const pt of chaves) {
    const agulha = semAcento(pt);
    let onde = semAcento(saida).indexOf(agulha);
    while (onde >= 0) {
      saida = saida.slice(0, onde) + ENQUADRAMENTO[pt] + saida.slice(onde + pt.length);
      const proximo = semAcento(saida).indexOf(agulha, onde + ENQUADRAMENTO[pt].length);
      onde = proximo;
    }
  }
  return saida;
}
